import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useGetSprintsQuery, usePublishBlogMutation, useDeleteSprintMutation } from '../store/sprintApi';
import StartSprint from './StartSprint';
import DataTable from './DataTable';
import Pagination from './Pagination';
import { BACKEND_BASE_URL } from '../config';

const ITEMS_PER_PAGE = 10;

const SprintHistory: React.FC = () => {
  const { data: sprints = [], error, isLoading } = useGetSprintsQuery();
  const [publishBlog] = usePublishBlogMutation();
  const [deleteSprint, { isLoading: isDeleting }] = useDeleteSprintMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stats = useMemo(() => {
    const total = sprints.length;
    const published = sprints.filter((s) => s.status === 'published').length;
    const started = sprints.filter((s) => s.status === 'started').length;
    const unpublished = sprints.filter((s) => s.status === 'unpublished').length;
    return { total, published, started, unpublished };
  }, [sprints]);

  const handleDownload = (filePath: string) => {
    if (!filePath) return;
    const downloadUrl = `${BACKEND_BASE_URL}/uploads/${filePath.split('/uploads/')[1] || filePath}`;
    window.open(downloadUrl, '_blank');
  };

  const handlePublish = async (sprintId: string) => {
    try {
      await publishBlog(sprintId).unwrap();
      toast.success('Publishing started. Forms will be assigned shortly.');
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to publish sprint');
    }
  };

  const handleDelete = async (sprintId: string, sprintName: string) => {
    if (!window.confirm(`Delete sprint "${sprintName}"? This can be restored from the database if needed.`)) return;
    setDeletingId(sprintId);
    try {
      await deleteSprint(sprintId).unwrap();
      toast.success(`Sprint "${sprintName}" deleted.`);
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to delete sprint');
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(sprints.length / ITEMS_PER_PAGE);
  const paginated = sprints.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Error loading sprints.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Sprints" value={stats.total} accent="bg-trident-gradient text-white" />
        <StatCard label="Published" value={stats.published} accent="bg-green-50 text-green-700 border border-green-200" />
        <StatCard label="In Progress" value={stats.started} accent="bg-yellow-50 text-yellow-700 border border-yellow-200" />
        <StatCard label="Unpublished" value={stats.unpublished} accent="bg-trident-gray-50 text-trident-gray-700 border border-trident-gray-200" />
      </div>

      <div>
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-xl text-trident-gray-900">Sprint History</h2>
            <p className="text-sm text-trident-gray-500">Total: {sprints.length} sprints</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2 shrink-0"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Start New Sprint
          </button>
        </div>

        <DataTable
          loading={isLoading}
          rows={paginated}
          rowKey={(s) => s._id}
          emptyMessage="No sprint history yet. Create your first sprint above."
          columns={[
            {
              key: 'sno', label: '#', width: '60px', align: 'center',
              render: (_r, i) => <span className="text-trident-gray-400 font-mono text-xs">{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</span>,
            },
            {
              key: 'name', label: 'Sprint Name',
              render: (s) => <div className="font-semibold text-trident-gray-900">{s.name}</div>,
            },
            {
              key: 'date', label: 'Date',
              render: (s) => <span className="text-sm text-trident-gray-600">{new Date(s.date).toLocaleDateString()}</span>,
            },
            {
              key: 'file', label: 'File',
              render: (s) => (
                <div className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span className="text-sm text-trident-gray-600 truncate max-w-[200px]">{s.fileName}</span>
                </div>
              ),
            },
            {
              key: 'preview', label: 'Preview', align: 'center',
              render: (s) => (
                <button
                  onClick={() => handleDownload(s.filePath)}
                  className="text-xs font-display font-semibold text-trident-orange hover:text-trident-orange-dark hover:underline inline-flex items-center gap-1"
                >
                  Open
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </button>
              ),
            },
            {
              key: 'action', label: 'Action', align: 'right',
              render: (s) => {
                const isPublished = s.status === 'published';
                const isStarted = s.status === 'started';
                const isDisabled = isPublished || isStarted;
                const isRowDeleting = isDeleting && deletingId === s._id;
                return (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handlePublish(s._id)}
                      disabled={isDisabled || isRowDeleting}
                      className={`font-display font-semibold text-xs px-4 py-1.5 rounded-lg transition-all ${
                        isPublished
                          ? 'bg-green-50 text-green-700 border border-green-200 cursor-default'
                          : isStarted
                            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 cursor-not-allowed'
                            : 'bg-trident-gradient text-white shadow-trident hover:shadow-trident-lg hover:-translate-y-0.5'
                      }`}
                    >
                      {isPublished ? '✓ Published' : isStarted ? '⏳ Publishing...' : 'Publish Sprint'}
                    </button>
                    <button
                      onClick={() => handleDelete(s._id, s.name)}
                      disabled={isStarted || isRowDeleting}
                      title={isStarted ? 'Cannot delete a sprint that is currently publishing' : 'Delete sprint'}
                      className="font-display font-semibold text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {isRowDeleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                );
              },
            },
          ]}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={sprints.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      <StartSprint isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, accent }) => (
  <div className={`rounded-2xl px-5 py-4 ${accent}`}>
    <div className="text-xs font-display font-semibold uppercase tracking-wider opacity-80">{label}</div>
    <div className="font-display font-bold text-3xl mt-1">{value}</div>
  </div>
);

export default SprintHistory;
